import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { useViewOnly } from '@/components/templates/app-shell'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet'
import {
  AddConnectionFeedback,
  AddConnectionForm,
  AddConnectionOAuthStart,
} from './connections/AddConnectionForm'
import { ConnectionTypeIcon, ConnectionTypePicker } from './connections/ConnectionTypePicker'
import { connectionTypeName } from './connections/types'
import {
  useAddConnection,
  useConnectionReturn,
  type PendingAuthorization,
} from './connections/useAddConnection'

export function AddConnectionFlow({ adding, onClose }: { adding: boolean; onClose: () => void }) {
  const viewOnly = useViewOnly()
  const oauth = useConnectionReturn(!viewOnly)
  return (
    <>
      {oauth.error && <Alert>{oauth.error}</Alert>}
      {oauth.loading && <p className="p-3 text-body-sm">Completing vendor authorisation…</p>}
      {!viewOnly && (adding || oauth.pending) && (
        <AddConnectionSheet
          pending={oauth.pending}
          onClose={() => {
            onClose()
            oauth.clear()
          }}
        />
      )}
    </>
  )
}

export function AddConnectionSheet({
  onClose,
  pending,
}: {
  onClose: () => void
  pending?: PendingAuthorization
}) {
  const state = useAddConnection(onClose, pending)
  const type = state.type
  const title = type ? connectionTypeName(type.type) : 'Add Connection'
  const oauthStart = type?.credential_method === 'oauth' && !pending
  return (
    <Sheet
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <SheetContent
        side="right"
        className="sm:max-w-lg"
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        {type && !pending && (
          <Button
            className="w-fit px-0 text-muted-foreground"
            variant="link"
            onClick={() => state.choose()}
          >
            ← All types
          </Button>
        )}
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            {type && <ConnectionTypeIcon type={type.type} />}
            {title}
          </SheetTitle>
          <SheetDescription className="sr-only">
            Connect an outside system to your association.
          </SheetDescription>
        </SheetHeader>
        <ConnectionTypesState state={state} pending={!!pending} onClose={onClose} />
        <AddConnectionFeedback state={state} />
        {type &&
          (oauthStart ? (
            <AddConnectionOAuthStart state={state} title={title} onClose={onClose} />
          ) : (
            <AddConnectionForm state={state} title={title} pending={!!pending} onClose={onClose} />
          ))}
      </SheetContent>
    </Sheet>
  )
}
function ConnectionTypesState({
  state,
  pending,
  onClose,
}: {
  state: ReturnType<typeof useAddConnection>
  pending: boolean
  onClose: () => void
}) {
  if (state.types.isPending)
    return <p className="text-body-sm text-muted-foreground">Loading connection types…</p>
  if (state.types.error)
    return (
      <Alert>
        {state.types.error.message}
        <Button variant="outline" onClick={() => void state.types.refetch()}>
          Try again
        </Button>
      </Alert>
    )
  if (state.type) return null
  if (pending)
    return (
      <Alert>
        This connection type is no longer available.
        <Button variant="outline" onClick={onClose}>
          Close
        </Button>
      </Alert>
    )
  return <ConnectionTypePicker types={state.types.data.connection_types} onChoose={state.choose} />
}
